// §6A fasted window — the acknowledgement an oral-semaglutide patient must make
// before checkout, and the routing that follows if they decline.
//
// ─────────────────────────────────────────────────────────────────────────────
// ⚠️  DARK. Every string below is CLINICAL COPY AWAITING BELUGA REVIEW.
//     Nothing here renders until REACT_APP_FASTED_WINDOW_ENABLED === '1'.
//     Do not flip that flag until §REVIEW below is signed off.
// ─────────────────────────────────────────────────────────────────────────────
//
// Why this exists (dashboard PRD §6A, owner-confirmed): oral semaglutide is not
// a preference-level difference from the injectables. It has a dosing protocol a
// patient either can or cannot live with — empty stomach on waking, no more than
// 4 oz of plain water, and a wait before any food, drink, or other oral
// medication. Orforglipron (Foundayo) carries no such restriction.
//
// That makes the oral cross-link a CLINICAL ROUTING DECISION rather than an
// upsell. A patient who cannot commit to the fasted window must be routed to
// Foundayo — never dead-ended, and never pushed back to an injection, which is a
// different decision they already declined by choosing a pill.
//
// So the decline path here does NOT disqualify. Every other consent decline in
// this flow ends at DisqualifiedStep; this one switches the product and carries
// on. That asymmetry is the point, not an oversight.

export const FASTED_WINDOW_ENABLED = process.env.REACT_APP_FASTED_WINDOW_ENABLED === '1';

// Where a decliner goes. Foundayo is orforglipron — an oral GLP-1 with no fasted
// requirement — so it is the only destination that keeps them on a pill.
export const FASTED_DECLINE_PRODUCT = 'foundayo-pill';

// ─── §REVIEW — what Beluga must confirm before the flag is flipped ───────────
//
//  1. THE WAIT DURATION. The copy says "at least 30 minutes". That figure comes
//     from oral-semaglutide labelling, NOT from the PRD, which deliberately said
//     only "wait before food/drink/any other oral med" with no number. It is the
//     one clinical specific this file introduces on its own. Confirm the number
//     or replace it — if Beluga would rather not state one, delete the phrase
//     rather than soften it.
//  2. THE WATER LIMIT. "No more than 4 oz (about half a cup) of plain water" —
//     confirm the volume and that "plain" is the right qualifier.
//  3. THE LEVOTHYROXINE QUESTION is recorded for the provider and does NOT gate
//     anything. Confirm that is right: if a morning empty-stomach medication
//     should instead block the oral or force a provider note, say so — the
//     question currently informs, it does not decide.
//  4. THE DECLINE ROUTE. Confirm Foundayo is the correct destination for a
//     patient who cannot commit, and that presenting it immediately (rather than
//     ending the visit) is acceptable.
//  5. THE CONFIRMATION REPEAT wording (§fastedCompletionCopy below).
//
// Until 1–5 are answered, this file is a draft and the flag stays off.
// ─────────────────────────────────────────────────────────────────────────────

export const FASTED_WINDOW_CONSENT_BODY = `Taking this medication correctly

This medication is a tablet form of semaglutide. For it to be absorbed, it has to be taken in a specific way every day:

- Take it first thing after waking, on an empty stomach.
- Swallow it whole with no more than 4 oz (about half a cup) of plain water. Not coffee, not juice, not water with anything added.
- Wait at least 30 minutes before eating, drinking anything else, or taking any other medication by mouth.
- Do not split, crush, or chew the tablet.

If you take it with food, with more than a small amount of water, or alongside another oral medication, your body may absorb little or none of it.

This is a daily routine, not an occasional instruction. Before you continue, please confirm you are able to follow it.

If this does not fit your mornings, that is a common and reasonable answer — and it does not mean you are out of options. We will show you an oral treatment that has no fasting requirement.`;

// The acknowledgement. Uses the existing consent-dropdown field type, per the
// PRD's explicit ask that it be an acknowledgement step rather than a paragraph
// the patient can skim past.
//
// NOTE the decline option carries `routesTo`, not `disqualifies`. Nothing else in
// this flow does that; see the header.
export const fastedWindowConsentStep = {
  id: 'fasted-window-ack',
  type: 'consent-dropdown',
  question: 'Taking an oral GLP-1 — what it requires',
  consentText: FASTED_WINDOW_CONSENT_BODY,
  options: [
    {
      label: 'I understand and I can take this on an empty stomach each morning',
      value: 'accept',
    },
    {
      label: 'I cannot commit to this — show me an option without a fasting requirement',
      value: 'decline',
      // Routes rather than disqualifies. Handled by the form, not by
      // ConsentDropdownStep, which only knows how to disqualify.
      routesTo: FASTED_DECLINE_PRODUCT,
    },
  ],
};

// Named explicitly because levothyroxine is the common collision: it ALSO has to
// be taken on an empty stomach on waking, so a patient on it cannot simply take
// both. The provider needs to know before prescribing. Recorded, not gated —
// see §REVIEW item 3.
export const morningOralMedsStep = {
  id: 'morning-oral-meds',
  type: 'single-select',
  question: 'Do you take any medication first thing in the morning on an empty stomach?',
  subtitle:
    'For example levothyroxine (Synthroid), which also has to be taken on an empty stomach after waking. Your provider needs to know so they can plan the timing with you.',
  options: [
    { label: 'Yes — levothyroxine (Synthroid)', value: 'levothyroxine' },
    { label: 'Yes — a different morning medication', value: 'other' },
    { label: 'No', value: 'no' },
  ],
};

// The confirmation-screen repeat (PRD §6A). Shown only when the product the
// patient actually ends on carries the restriction — a decliner switched to
// Foundayo must NOT see it, or we have just told them to fast for no reason.
export function fastedCompletionCopy(hasFastedWindow) {
  if (!hasFastedWindow) return null;
  return 'Remember: take your tablet first thing after waking, on an empty stomach, with no more than 4 oz of plain water — then wait at least 30 minutes before eating, drinking anything else, or taking other oral medication.';
}

/**
 * Does this product require the fasted acknowledgement?
 * Reads the flag AND the product, so a product-conditional slide can never
 * appear while the copy is unreviewed.
 * @param {object|null} product a BRANDED_PRODUCTS entry
 */
export const requiresFastedWindow = (product) =>
  FASTED_WINDOW_ENABLED && Boolean(product && product.fastedWindow);

/**
 * The option the patient chose, if it routes somewhere.
 * @returns {string|null} destination slug, or null
 */
export function fastedDeclineDestination(answer) {
  const opt = fastedWindowConsentStep.options.find((o) => o.value === answer);
  return (opt && opt.routesTo) || null;
}
